import { inject, Injectable, Injector } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { InboxService } from '@galaxy/conversation/core';
import {
  getNavigationData$ as getNavigationData,
  getSalesInfo$ as getSalesInfo,
  getDefaultLocation$,
  setDefaultLocation$,
} from '@vendasta/protos/atlas/v1/atlas';
import { getNavigationDataResponseFromJson, getNavigationDataResponseToJson } from '@vendasta/protos/atlas/v1/codec';
import { PlatformMode, UserViewType } from '@vendasta/protos/atlas/v1/enums';
import type {
  DropdownItem,
  ExitLinkConfiguration,
  GetNavigationDataResponse,
  GetSalesInfoResponse,
  PinnedItem,
  SalesContact,
  SideNavigationItem,
} from '@vendasta/protos/atlas/v1/types';
import {
  Observable,
  Subject,
  catchError,
  combineLatest,
  defer,
  distinctUntilChanged,
  filter,
  map,
  merge,
  of,
  race,
  retry,
  shareReplay,
  startWith,
  switchMap,
  take,
  tap,
  throwError,
  timer,
} from 'rxjs';
import { WhitelabelService } from '@galaxy/partner';
import { BusinessNavConfigService, Config } from './config.service';
import { BusinessNavHostBridge, BusinessNavHostError, isAbsoluteHttpUrl } from './host-bridge';
import { flattenNavigationLinks } from './navigation-links';
import { signOutLabel } from './globals';

const BRANDS_FEATURE = 'brands';

interface CachedGetNavigationDataResponse {
  locationId: string;
  response: GetNavigationDataResponse;
}

const TOKEN_CHANGE_WAIT_MS = 10_000;

function statusOf(err: unknown): number | undefined {
  return (err as { status?: number })?.status;
}

function toHostError(status: number): BusinessNavHostError {
  return { reason: status === 401 || status === 403 ? 'auth' : 'data', status };
}

const DEFAULT_IMAGE_URL =
  'https://lh3.googleusercontent.com/70DGcv8fyAzy1n_cWh8GyVbsCIWzPGZMdBFiwJa8Ot-Ss5Rf_oZY8hKOG2wAttY4_iuU5TlO9NLlDrBGuEJBj6DHwRnGd2CJ32J2Im3c1g';

@Injectable({ providedIn: 'root' })
export class BusinessNavDataService {
  private readonly bridge = inject(BusinessNavHostBridge);
  private readonly platformMode = Capacitor.isNativePlatform() ? PlatformMode.MOBILE : PlatformMode.WEB;
  private navigationData$ = this.cfgService.config$.pipe(
    filter((config) => !!config && (!!config.accountId || !!config.groupPath)),
    distinctUntilChanged((a, b) => {
      return (
        a.accountId === b.accountId &&
        a.groupPath === b.groupPath &&
        a.partnerId === b.partnerId &&
        a.marketId === b.marketId
      );
    }),
    switchMap((config) => {
      const locationId = BusinessNavDataService.locationIdFromConfig(config);
      const lastResponse = this.getLastResponse();
      return this.loadNavigationData({
        accountGroupId: config.accountId,
        groupPath: config.groupPath,
        partnerId: config.partnerId,
        marketId: config.marketId,
        platformMode: this.platformMode,
      }).pipe(
        tap((response) => this.rememberLastResponse({ locationId, response })),
        tap((response) => this.reportRelativeLinks(response)),
        startWith(lastResponse?.locationId === locationId ? lastResponse.response : ('LOADING' as const)),
      );
    }),
    shareReplay({ refCount: false, bufferSize: 1 }),
    filter((res): res is GetNavigationDataResponse => res !== 'LOADING'),
  );

  readonly navigationLinks$ = this.navigationData$.pipe(
    map((res) => flattenNavigationLinks(res?.navigationItems || [])),
  );
  readonly navigationIds$ = this.navigationLinks$.pipe(map((links) => links.map((link) => link.navigationId)));

  readonly linksWithSublinks$ = this.navigationData$.pipe(
    map((res) => res?.navigationItems || []),
    map((allItems: SideNavigationItem[]) => {
      return allItems
        .reduce(
          (accumulator, item) =>
            item.sideNavigationLink
              ? [...accumulator, item]
              : item.sideNavigationContainer
                ? [...accumulator, ...item.sideNavigationContainer.sideNavigationItems]
                : accumulator,
          [] as SideNavigationItem[],
        )
        .filter((item) => item.sideNavigationLink && item.sideNavigationLink.subLinks?.length > 0)
        .map((item) => item.sideNavigationLink);
    }),
  );

  private salesInfo$: Observable<GetSalesInfoResponse>;
  public disableBusinessNav$: Observable<boolean>;
  public disableProductSwitcher$: Observable<boolean>;
  public businessCenterName$: Observable<string>;
  public partnerName$: Observable<string>;
  public cobrandingLogoURL$: Observable<string>;
  public marketName$: Observable<string>;
  public marketLogoUrl$: Observable<string>;
  public darkMarketLogoUrl$: Observable<string>;
  public salespersonName$: Observable<string>;
  public salespersonPictureUrl$: Observable<string>;
  public currentBrandName$: Observable<string>;
  public salespersonContact$: Observable<SalesContact>;
  public hasSalesperson$: Observable<boolean>;
  public contactUsModalDescription$: Observable<string>;
  public hideSalespersonContact$: Observable<boolean>;
  public showSupportCenterLink$: Observable<boolean>;
  public supportCenterLinkUrl$: Observable<string>;
  public supportCenterLinkLabel$: Observable<string>;
  public canAccessInbox$: Observable<boolean>;
  public navigationItems$: Observable<SideNavigationItem[]>;
  public dropdownItems$: Observable<DropdownItem[]>;
  public pinnedItems$: Observable<PinnedItem[]>;
  public language$: Observable<string>;
  public userViewType$: Observable<UserViewType>;
  public locationSwitchable$: Observable<boolean>;
  public showGroupsTab$: Observable<boolean>;
  public businessAppBranding$: Observable<boolean>;
  public exitLinkConfiguration$: Observable<ExitLinkConfiguration>;

  public userId$: Observable<string>;
  public readonly defaultLocation$: Observable<{
    accountGroupId?: string;
    groupId?: string;
  }>;
  private defaultLocationUpdate$$ = new Subject<{ accountGroupId: string; groupId: string }>();

  private static readonly LAST_RESPONSE_KEY = 'business-nav-last-response';

  static clearLocationCache(): void {
    localStorage.removeItem(BusinessNavDataService.LAST_RESPONSE_KEY);
  }

  private rememberLastResponse(res: CachedGetNavigationDataResponse): void {
    if (this.bridge.isExternal()) return;
    localStorage.setItem(
      BusinessNavDataService.LAST_RESPONSE_KEY,
      JSON.stringify({
        locationId: res.locationId,
        response: getNavigationDataResponseToJson(res.response),
      }),
    );
  }

  private getLastResponse(): CachedGetNavigationDataResponse {
    if (this.bridge.isExternal()) return null;
    const o: { locationId: string; response: object } = JSON.parse(
      localStorage.getItem(BusinessNavDataService.LAST_RESPONSE_KEY),
    );
    if (!o || !o.response) {
      return null;
    }
    return {
      locationId: o.locationId,
      response: getNavigationDataResponseFromJson(o.response as Record<string, unknown>),
    };
  }

  // In external mode a failure is reported and the stream stays alive: the next token from the host restarts the fetch.
  private loadNavigationData(request: Parameters<typeof getNavigationData>[0]): Observable<GetNavigationDataResponse> {
    return getNavigationData(request).pipe(
      this.retryOnceAfterTokenChange(),
      catchError((err) => {
        if (!this.bridge.isExternal()) return throwError(() => err);
        const status = statusOf(err);
        // a request that never got a token was already reported by the bearer interceptor
        if (status !== undefined) this.bridge.reportError(toHostError(status));
        return this.bridge.tokenChanged$.pipe(
          take(1),
          switchMap(() => this.loadNavigationData(request)),
        );
      }),
    );
  }

  private reportRelativeLinks(response: GetNavigationDataResponse): void {
    if (!this.bridge.isExternal()) return;
    if (flattenNavigationLinks(response.navigationItems || []).some((link) => !isAbsoluteHttpUrl(link.url))) {
      this.bridge.reportError({ reason: 'config' });
    }
  }

  private retryOnceAfterTokenChange<T>() {
    return (source: Observable<T>): Observable<T> => {
      if (!this.bridge.isExternal()) return source;
      return source.pipe(
        retry({
          count: 1,
          delay: (err) =>
            statusOf(err) === 401
              ? race(
                  this.bridge.tokenChanged$.pipe(take(1)),
                  timer(TOKEN_CHANGE_WAIT_MS).pipe(switchMap(() => throwError(() => err))),
                )
              : throwError(() => err),
        }),
      );
    };
  }

  private static locationIdFromConfig(cfg: Config): string {
    return cfg.accountId ? cfg.accountId : cfg.groupPath;
  }

  constructor(
    private cfgService: BusinessNavConfigService,
    private whitelabelService: WhitelabelService,
    private injector: Injector,
  ) {
    this.salesInfo$ = this.cfgService.config$.pipe(
      filter((config: Config) => !!config && (!!config.accountId || !!config.groupPath)),
      distinctUntilChanged(),
      switchMap((config: Config) =>
        getSalesInfo({
          accountGroupId: config.accountId,
          groupPath: config.groupPath,
        }).pipe(filter((res: GetSalesInfoResponse) => JSON.stringify(res) !== '{}')),
      ),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.dropdownItems$ = combineLatest([this.cfgService.serviceProviderId$, this.navigationData$]).pipe(
      map(([serviceProviderId, res]: [string, GetNavigationDataResponse]) => {
        if (!res.dropdownItems) {
          return [];
        }
        // Disable all dropdown items except for logout when business nav is disabled
        if (serviceProviderId !== 'VBC' && res.disableBusinessNav) {
          return res.dropdownItems.filter(
            (dropdownItem: DropdownItem) => dropdownItem.translationId === 'NAVIGATION.MENU.SIGN_OUT',
          );
        }
        return res.dropdownItems;
      }),
      startWith([
        {
          label: signOutLabel,
          translationId: 'NAVIGATION.MENU.SIGN_OUT',
          url: '/logout/',
        } as DropdownItem,
      ]),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.disableBusinessNav$ = this.navigationData$.pipe(
      filter((res: GetNavigationDataResponse) => !!res.disableBusinessNav),
      map((res: GetNavigationDataResponse) => res.disableBusinessNav),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.disableProductSwitcher$ = this.navigationData$.pipe(
      filter((res: GetNavigationDataResponse) => !!res.disableProductSwitcher),
      map((res: GetNavigationDataResponse) => res.disableProductSwitcher),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.exitLinkConfiguration$ = this.navigationData$.pipe(
      filter((res: GetNavigationDataResponse) => !!res.branding),
      map((res: GetNavigationDataResponse) => res.branding.exitLinkConfiguration),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.businessCenterName$ = this.navigationData$.pipe(
      filter((res) => !!res.branding),
      map((res) => res.branding.centerName),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.marketLogoUrl$ = this.navigationData$.pipe(
      filter((res) => !!res.branding),
      map((res) => res.branding.logoUrl),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.darkMarketLogoUrl$ = this.navigationData$.pipe(
      filter((res) => !!res.branding),
      map((res) => res.branding.darkModeLogoUrl),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.partnerName$ = this.navigationData$.pipe(
      filter((res) => !!res.branding),
      map((res) => res.branding.partnerName),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.cobrandingLogoURL$ = this.navigationData$.pipe(
      filter((res) => !!res.branding),
      map((res) => res.branding.cobrandingLogoUrl),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.marketName$ = this.navigationData$.pipe(
      filter((res) => !!res.branding),
      map((res) => res.branding.marketName),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.salespersonName$ = this.salesInfo$.pipe(
      filter((res) => !!res.salesInfo && !!res.salesInfo.salesContact),
      map((res) => {
        const { firstName, lastName } = res.salesInfo.salesContact;
        if (!firstName) {
          return '';
        } else {
          return firstName + (lastName ? ' ' + lastName : '');
        }
      }),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );
    this.salespersonPictureUrl$ = this.salesInfo$.pipe(
      filter((res) => !!res.salesInfo?.salesContact?.photoUrlSecure),
      map((res) => res.salesInfo.salesContact.photoUrlSecure || DEFAULT_IMAGE_URL),
      startWith(DEFAULT_IMAGE_URL),
    );

    this.salespersonContact$ = this.salesInfo$.pipe(
      filter((res) => !!res.salesInfo && !!res.salesInfo.salesContact),
      map((res) => res.salesInfo.salesContact),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.navigationItems$ = this.navigationData$.pipe(
      map((res) => res.navigationItems),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.pinnedItems$ = this.navigationData$.pipe(
      map((res) => res.pinnedItems),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.language$ = this.navigationData$.pipe(
      map((res) => res.language),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.currentBrandName$ = this.navigationData$.pipe(
      map((res) => res.currentBrandName),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.userViewType$ = this.navigationData$.pipe(
      map((res) => {
        if (!res.userView) {
          return UserViewType.USER_VIEW_TYPE_SMB;
        }
        return res.userView;
      }),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.locationSwitchable$ = this.navigationData$.pipe(
      map((res) => {
        return (
          res.userView === UserViewType.USER_VIEW_TYPE_ADMIN ||
          (res.associatedLocationIds.accountGroupIds?.length ?? 0) +
            (res.associatedLocationIds.groupPaths?.length ?? 0) >
            1
        );
      }),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.userId$ = this.navigationData$.pipe(
      map((resp: GetNavigationDataResponse) => resp.userId),
      distinctUntilChanged(),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.defaultLocation$ = merge(
      this.defaultLocationUpdate$$,
      this.cfgService.partnerId$.pipe(switchMap((partnerId) => getDefaultLocation$({ partnerId }))),
    ).pipe(shareReplay({ refCount: true, bufferSize: 1 }));

    this.showGroupsTab$ = this.cfgService.partnerId$.pipe(
      switchMap((partnerId) => this.whitelabelService.getConfiguration(partnerId)),
      map((config) => (config.enabledFeatures ?? []).includes(BRANDS_FEATURE)),
      shareReplay({ refCount: false, bufferSize: 1 }),
    );

    this.businessAppBranding$ = this.navigationData$.pipe(
      map((data) => data.businessAppBranding ?? false),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.hasSalesperson$ = this.salesInfo$.pipe(
      map((res) => !!res.salesInfo && !!res.salesInfo.salesContact),
      startWith(false),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    const contactUsModalConfig$ = this.salesInfo$.pipe(
      map((res) => res.contactUsModalConfig),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    // Each field seeds its default so consumers get a single source of truth that resolves even when
    // getSalesInfo returns {} (salesInfo$ is filtered on non-empty, so it never emits then). Consumers
    // must not re-seed these, and combineLatest gates that depend on them cannot stall.
    const configField = <T>(select: (config: GetSalesInfoResponse['contactUsModalConfig']) => T, seed: T) =>
      contactUsModalConfig$.pipe(map(select), startWith(seed), shareReplay({ refCount: true, bufferSize: 1 }));

    this.contactUsModalDescription$ = configField((config) => (config?.contactUsModalDescription ?? '').trim(), '');
    this.hideSalespersonContact$ = configField((config) => config?.hideSalespersonContact ?? false, false);
    this.showSupportCenterLink$ = configField((config) => config?.showSupportCenterLink ?? false, false);
    this.supportCenterLinkUrl$ = configField((config) => config?.supportCenterLinkUrl ?? '', '');
    this.supportCenterLinkLabel$ = configField((config) => config?.supportCenterLinkLabel ?? '', '');

    const accountHasInboxEnabled$ = this.navigationItems$.pipe(
      map((navItems) => !!navItems?.find((navItem) => navItem?.sideNavigationLink?.navigationId === 'nav-inbox')),
    );

    // InboxService is resolved lazily rather than constructor-injected: it depends (via the
    // Conversation config token and PageAccessService) back on BusinessNavDataService, so injecting it
    // here forms a DI cycle (NG0200). Deferring the lookup to subscription time breaks the cycle, as
    // BusinessNavDataService is already constructed by then.
    const allowSmbChatWithPartner$ = defer(
      () => this.injector.get(InboxService, null)?.allowSmbChatWithPartner$ ?? of(false),
    );

    this.canAccessInbox$ = combineLatest([allowSmbChatWithPartner$, accountHasInboxEnabled$]).pipe(
      map(([allowSmbChat, accountHasInboxEnabled]) => allowSmbChat && accountHasInboxEnabled),
      startWith(false),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );
  }

  setDefaultLocation(accountGroupId: string, groupId: string): Observable<void> {
    return this.cfgService.partnerId$.pipe(
      switchMap((partnerId) => setDefaultLocation$({ partnerId, accountGroupId, groupId })),
      tap(() => this.defaultLocationUpdate$$.next({ accountGroupId, groupId })),
      take(1),
    );
  }
}
